import { Flex, Result, Spin } from "antd"
import React, {
  Suspense,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState
} from "react"
import { useTranslation } from "react-i18next"

import { DownloadCopy } from "../../../components/Esign/signing/DownloadCopy.tsx"
import { LazySignDocument } from "../../../components/Esign/signing/index.ts"
import {
  certificateName,
  signingIsOpen
} from "../../../components/Esign/signing/types.ts"
import type {
  Outcome,
  SigningState
} from "../../../components/Esign/signing/index.ts"
import { createPublicTransport } from "../api/signing.transport"
import type { RefusalReason, SigningRefusal } from "../types/signing.types"
import SigningDocumentView from "./SigningDocumentView"

interface Props {
  session: string
  documentUrl: string | null
  documentBlob: Blob | null
  documentName: string
  recipientName: string
  isSigner: boolean
  completed: boolean
  onMaySign: (maySign: boolean) => void
  onDeclined: () => void
  onRefused: (refusal: SigningRefusal) => void
}

type Ending = "signing" | "signed"

// A dead link, as opposed to one action refused, which the signing screen words itself.
const DEAD_LINK: readonly RefusalReason[] = [
  "envelope_not_live",
  "expired",
  "revoked",
  "consent_withdrawn",
  "parent_archived"
]

const deadLink = (outcome: Outcome<unknown>): SigningRefusal | null => {
  const reason = DEAD_LINK.find((known) => known === outcome.reason)
  return reason ? { reason, detail: outcome.detail ?? undefined } : null
}

const DocumentStep: React.FC<Props> = ({
  session,
  documentUrl,
  documentBlob,
  documentName,
  recipientName,
  isSigner,
  completed,
  onMaySign,
  onDeclined,
  onRefused
}) => {
  const { t } = useTranslation()
  const [ending, setEnding] = useState<Ending>("signing")
  // Read once: a later copy must not build a second transport, which would fetch again.
  const held = useRef(documentBlob)
  const transport = useMemo(
    () => createPublicTransport(session, held.current),
    [session]
  )

  // A new session is a new sitting, even when the step itself stays mounted.
  useEffect(() => {
    setEnding("signing")
  }, [session])

  const download = useCallback(
    () => transport.download(documentName),
    [transport, documentName]
  )

  const downloadCertificate = useCallback(
    () => transport.downloadCertificate(certificateName(documentName)),
    [transport, documentName]
  )

  const onStateLoaded = useCallback(
    (state: SigningState) => onMaySign(signingIsOpen(state)),
    [onMaySign]
  )

  const onFailed = useCallback(
    (outcome: Outcome<unknown>) => {
      const refusal = deadLink(outcome)
      if (refusal) onRefused(refusal)
    },
    [onRefused]
  )

  if (isSigner) {
    if (ending === "signed") {
      return (
        <Flex vertical gap={16}>
          <Result
            status="success"
            title={t("signing.sign.signedTitle")}
            subTitle={t("signing.sign.signedBody")}
          />
          <DownloadCopy download={download} label={t("signing.sign.download")} />
        </Flex>
      )
    }

    return (
      <Suspense
        fallback={
          <Flex justify="center" style={{ padding: "48px 0" }}>
            <Spin size="large" />
          </Flex>
        }
      >
        <LazySignDocument
          transport={transport}
          signerName={recipientName}
          documentName={documentName}
          onStateLoaded={onStateLoaded}
          onFailed={onFailed}
          onSigned={() => {
            onMaySign(false)
            setEnding("signed")
          }}
          onDeclined={onDeclined}
        />
      </Suspense>
    )
  }

  if (!documentUrl) {
    return (
      <Flex justify="center" style={{ padding: "48px 0" }}>
        <Spin size="large" />
      </Flex>
    )
  }

  return (
    <Flex vertical gap={16}>
      <SigningDocumentView
        session={session}
        documentUrl={documentUrl}
        documentBlob={documentBlob}
        height="60vh"
      />
      <DownloadCopy
        download={download}
        label={t("signing.document.download")}
      />
      {completed && (
        <DownloadCopy
          download={downloadCertificate}
          label={t("signing.certificate.download")}
          failedText={t("signing.certificate.failed")}
        />
      )}
    </Flex>
  )
}

export default DocumentStep
